import React, { useCallback } from 'react';

import { Box, Text } from '@atlaskit/primitives/compiled';
import Range from '@atlaskit/range';

import { useDataBinding } from '../../hooks/useDataBinding.tsx';
import { useDynamicString, useDynamicNumber } from '../../hooks/useDynamicValue.tsx';
import { isDataBinding } from '../../types/A2UICommon.types.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';
import type { A2UISliderProps } from '../../types/A2UIComponents.types.tsx';

type Props = {
	definition: A2UIComponentDefinition;
};

export const A2UISlider = ({ definition }: Props): React.JSX.Element => {
	const def = definition as A2UISliderProps;
	const label = useDynamicString(def.label);

	// Two-way binding for number value
	const bindingPath = isDataBinding(def.value) ? def.value.path : null;
	const [boundValue, setBoundValue] = useDataBinding<number>(bindingPath ?? '/___noop');
	const staticValue = useDynamicNumber(def.value);

	const value = bindingPath ? (boundValue ?? def.min) : staticValue;

	const handleChange = useCallback(
		(newValue: number) => {
			if (bindingPath) {
				setBoundValue(newValue);
			}
		},
		[bindingPath, setBoundValue],
	);

	const fieldId = `a2ui-slider-${def.id}`;

	return (
		<Box>
			{label && (
				<label htmlFor={fieldId}>
					<Text as="span" weight="semibold">
						{label}
					</Text>
				</label>
			)}
			<Range
				id={fieldId}
				min={def.min}
				max={def.max}
				value={value}
				onChange={handleChange}
				aria-label={def.accessibility?.label ?? label}
			/>
		</Box>
	);
};
